import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { WhyThisMatters } from '../components/Shared.jsx';

// Clean up internal source labels (e.g., "serperContacts.mediaKit" → "Google Search")
function formatSource(raw) {
  if (!raw) return '';
  if (/^serper/i.test(raw)) return 'Google Search';
  return raw;
}

export function ContactResearch({ data }) {
  const { contactCards = [], priorityOutreach = [], summary } = data;
  const purposeColor = (p) =>
    p === 'advertising' || p === 'sponsorship' ? T.primary : p === 'press' ? T.accent : T.textSecondary;

  return (
    <Box>
      {priorityOutreach.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.primary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Priority Outreach</Typography>
          {priorityOutreach.map((po, i) => (
            <Box key={i} sx={{ mb: 1, p: 1.5, background: 'rgba(249,115,22,0.07)', border: `1px solid rgba(249,115,22,0.25)`, borderRadius: 1, display: 'flex', gap: 1.5 }}>
              <Box sx={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: T.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, mt: 0.25 }}>{po.priority}</Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text }}>{po.contact}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                  <Box sx={{ px: 1, py: 0.25, background: T.surface3, color: T.textSecondary, borderRadius: 4, fontSize: 11, border: `1px solid ${T.border}` }}>{po.type}</Box>
                  <Typography sx={{ fontSize: 12, color: T.textSecondary }}>{po.reasoning}</Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {contactCards.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Contact Cards</Typography>
          {contactCards.map((c, i) => (
            <Box key={i} sx={{ mb: 1.5, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Box sx={{ px: 1, py: 0.25, background: T.surface3, color: purposeColor(c.purpose), borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', border: `1px solid ${T.border}` }}>{c.purpose}</Box>
                <Box sx={{ px: 1, py: 0.25, background: c.confidence === 'high' ? 'rgba(34,197,94,0.15)' : c.confidence === 'medium' ? 'rgba(234,179,8,0.15)' : T.surface3, color: c.confidence === 'high' ? T.success : c.confidence === 'medium' ? T.warning : T.textSecondary, borderRadius: 4, fontSize: 11 }}>{c.confidence}</Box>
              </Box>
              <Typography
                component={c.value?.startsWith('http') ? 'a' : c.type === 'email' ? 'a' : 'span'}
                href={c.value?.startsWith('http') ? c.value : c.type === 'email' ? `mailto:${c.value}` : undefined}
                target={c.value?.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                sx={{ fontSize: 14, fontWeight: 600, color: T.accent, wordBreak: 'break-all', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {c.value}
              </Typography>
              <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.5 }}>
                Source: {formatSource(c.source)}
                {c.sourceUrl && (
                  <>
                    {' — '}
                    <Typography component="a" href={c.sourceUrl} target="_blank" rel="noreferrer" sx={{ fontSize: 12, color: T.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {c.sourceUrl}
                    </Typography>
                  </>
                )}
              </Typography>
              {c.recommendedOutreach && <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.25, fontStyle: 'italic' }}>{c.recommendedOutreach}</Typography>}
            </Box>
          ))}
        </Box>
      )}

      {summary && (
        <Box sx={{ mb: 3, p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13, color: T.textSecondary }}>{summary}</Typography>
        </Box>
      )}

      <WhyThisMatters text="Consolidated contact intelligence accelerates outreach and partnership opportunities." citations={data.expertCitations} />
    </Box>
  );
}
